import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { z } from "zod";

export type ManualNotificationRow = {
  id: string;
  manual_id: string;
  version: number;
  audience: string;
  role_scope: string | null;
  slug: string | null;
  title_i18n: Record<string, string>;
  description_i18n: Record<string, string>;
  created_at: string;
  read_at: string | null;
};

/** List notifications visible to the current user (unread first). */
export const listMyManualNotifications = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<ManualNotificationRow[]> => {
    // Respect the user's in-app preference for the "manuals" category.
    const { data: pref } = await context.supabase
      .from("user_notification_prefs")
      .select("in_app")
      .eq("user_id", context.userId)
      .eq("category", "manuals")
      .maybeSingle();
    if (pref && pref.in_app === false) return [];

    const { data: notifs, error } = await context.supabase
      .from("manual_notifications")
      .select("id, manual_id, version, audience, role_scope, slug, title_i18n, description_i18n, created_at")
      .order("created_at", { ascending: false })
      .limit(50);
    if (error) throw new Error(error.message);
    const ids = (notifs ?? []).map((n: any) => n.id);
    let readMap: Record<string, string> = {};
    if (ids.length) {
      const { data: reads } = await context.supabase
        .from("manual_notification_reads")
        .select("notification_id, read_at")
        .eq("user_id", context.userId)
        .in("notification_id", ids);
      for (const r of reads ?? []) readMap[r.notification_id] = r.read_at;
    }
    return (notifs ?? []).map((n: any) => ({ ...n, read_at: readMap[n.id] ?? null }));
  });

/** Mark a single notification as read. */
export const markManualNotificationRead = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => z.object({ id: z.string().uuid() }).parse(d))
  .handler(async ({ data, context }) => {
    const { error } = await context.supabase
      .from("manual_notification_reads")
      .upsert({ notification_id: data.id, user_id: context.userId });
    if (error) throw new Error(error.message);
    return { ok: true };
  });

/** Mark all currently-visible notifications as read. */
export const markAllManualNotificationsRead = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data: notifs } = await context.supabase
      .from("manual_notifications")
      .select("id");
    const rows = (notifs ?? []).map((n: any) => ({
      notification_id: n.id,
      user_id: context.userId,
    }));
    if (rows.length) {
      const { error } = await context.supabase
        .from("manual_notification_reads")
        .upsert(rows);
      if (error) throw new Error(error.message);
    }
    return { ok: true, count: rows.length };
  });
